import { useMemo } from 'react';
import { lengthenId } from '@beamible/uuid';
import useGroupSupporters from '@pkg/api/designs/queries/useGroupSupporters';
import useSupporterMutations from './useSupporterMutations';

/**
 * The users supporting one group.
 *
 * The store holds ids short and the route names them whole, so they are
 * lengthened here, for the read and the writes alike. The route answers with the people
 * rather than their ids, so there is nothing left to look up — only the display
 * name the components read, derived the way people derive theirs.
 *
 * @param {String} designId
 * @param {String} groupId
 * @return {Object}
 */
export default function useSupporters(designId, groupId, enabled = true) {
  const design = designId ? lengthenId(designId) : null;
  const group = groupId ? lengthenId(groupId) : null;

  const { supporters, isLoading } = useGroupSupporters(design, group, enabled);

  const mutations = useSupporterMutations(design, group);

  return useMemo(() => {
    const ids = [];
    const list = [];

    supporters.forEach((supporter) => {
      const user = {
        ...supporter,
        __name:
          `${supporter.first_name ?? ''} ${supporter.last_name ?? ''}`.trim(),
      };

      ids.push(user.id);
      list.push(user);
    });

    return Object.freeze({ ids, isLoading, list, mutations });
  }, [supporters, isLoading, mutations]);
}
